/**
 * DebiCheck Maintenance Validation Utilities
 * Based on the API specification for DebiCheck maintenance operations
 */

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validate amount for DebiCheck operations
 * @param amount - The amount to validate
 * @returns ValidationResult
 */
export const validateAmount = (amount: number): ValidationResult => {
  const errors: string[] = [];

  if (amount === null || amount === undefined) {
    errors.push("Amount is required");
  } else if (typeof amount !== "number") {
    errors.push("Amount must be a number");
  } else if (amount <= 0) {
    errors.push("Amount must be greater than 0");
  } else if (!/^\d+(\.\d{1,2})?$/.test(amount.toString())) {
    errors.push("Amount must have at most 2 decimal places");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate collection day (1-31)
 * @param day - The day to validate
 * @returns ValidationResult
 */
export const validateCollectionDay = (day: number): ValidationResult => {
  const errors: string[] = [];

  if (day === null || day === undefined) {
    errors.push("Collection day is required");
  } else if (!Number.isInteger(day)) {
    errors.push("Collection day must be an integer");
  } else if (day < 1 || day > 31) {
    errors.push("Collection day must be between 1 and 31");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate South African account number
 * @param accountNumber - The account number to validate
 * @returns ValidationResult
 */
export const validateAccountNumber = (accountNumber: string): ValidationResult => {
  const errors: string[] = [];

  if (!accountNumber) {
    errors.push("Account number is required");
  } else if (typeof accountNumber !== "string") {
    errors.push("Account number must be a string");
  } else if (!/^\d{9,11}$/.test(accountNumber)) {
    errors.push("Account number must be 9-11 digits");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate account type
 * @param accountType - The account type to validate
 * @returns ValidationResult
 */
export const validateAccountType = (accountType: string): ValidationResult => {
  const errors: string[] = [];
  const validTypes = [
    "1",
    "2",
    "Savings",
    "Current",
    "Cheque",
    "Cheque/Current",
  ];

  if (!accountType) {
    errors.push("Account type is required");
  } else if (!validTypes.includes(accountType)) {
    errors.push(
      'Account type must be "1" (Cheque/Current), "2" (Savings), or Savings/Current'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate South African bank name
 * @param bank - The bank name to validate
 * @returns ValidationResult
 */
export const validateBank = (bank: string): ValidationResult => {
  const errors: string[] = [];
  const validBankIds = ["1", "2", "3", "7", "10", "16", "44", "55", "61", "Access"];
  const validBankNames = [
    "ABSA",
    "FNB",
    "Standard Bank",
    "SBSA",
    "Nedbank",
    "Capitec",
    "African Bank",
    "Investec",
    "Discovery Bank",
    "TymeBank",
    "Bidvest Bank",
    "FinBond",
    "Access",
  ];

  if (!bank) {
    errors.push("Bank is required");
  } else if (typeof bank !== "string") {
    errors.push("Bank must be a string");
  } else if (
    !validBankIds.includes(bank) &&
    !validBankNames.includes(bank)
  ) {
    errors.push(
      `Bank must be a NuPay bank id (${validBankIds.join(", ")}) or one of: ${validBankNames.join(", ")}`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate South African branch code
 * @param branchCode - The branch code to validate
 * @returns ValidationResult
 */
export const validateBranchCode = (branchCode: string): ValidationResult => {
  const errors: string[] = [];

  if (!branchCode) {
    errors.push("Branch code is required");
  } else if (typeof branchCode !== "string") {
    errors.push("Branch code must be a string");
  } else if (!/^\d{6}$/.test(branchCode)) {
    errors.push("Branch code must be exactly 6 digits");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate reason string
 * @param reason - The reason to validate
 * @returns ValidationResult
 */
export const validateReason = (reason: string): ValidationResult => {
  const errors: string[] = [];
  const maxLength = 255;

  if (!reason) {
    errors.push("Reason is required");
  } else if (typeof reason !== "string") {
    errors.push("Reason must be a string");
  } else if (reason.trim().length === 0) {
    errors.push("Reason cannot be empty or only whitespace");
  } else if (reason.length > maxLength) {
    errors.push(`Reason must be less than ${maxLength} characters`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate complete amount adjustment payload
 * @param data - The amount adjustment data
 * @returns ValidationResult
 */
export const validateAmountAdjustment = (data: {
  Amount: number;
  Reason: string;
}): ValidationResult => {
  const allErrors: string[] = [];

  const amountValidation = validateAmount(data.Amount);
  const reasonValidation = validateReason(data.Reason);

  if (!amountValidation.isValid) {
    allErrors.push(...amountValidation.errors);
  }
  if (!reasonValidation.isValid) {
    allErrors.push(...reasonValidation.errors);
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
};

/**
 * Validate complete collection day change payload
 * @param data - The collection day change data
 * @returns ValidationResult
 */
export const validateCollectionDayChange = (data: {
  CollectionDay: number;
  Reason: string;
}): ValidationResult => {
  const allErrors: string[] = [];

  const dayValidation = validateCollectionDay(data.CollectionDay);
  const reasonValidation = validateReason(data.Reason);

  if (!dayValidation.isValid) {
    allErrors.push(...dayValidation.errors);
  }
  if (!reasonValidation.isValid) {
    allErrors.push(...reasonValidation.errors);
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
};

/**
 * Validate complete account change payload
 * @param data - The account change data
 * @returns ValidationResult
 */
export const validateAccountChange = (data: {
  AccountNumber: string;
  AccountType: string;
  Bank: string;
  BranchCode: string;
  Reason: string;
}): ValidationResult => {
  const allErrors: string[] = [];

  const accountNumberValidation = validateAccountNumber(data.AccountNumber);
  const accountTypeValidation = validateAccountType(data.AccountType);
  const bankValidation = validateBank(data.Bank);
  const branchCodeValidation = validateBranchCode(data.BranchCode);
  const reasonValidation = validateReason(data.Reason);

  if (!accountNumberValidation.isValid) {
    allErrors.push(...accountNumberValidation.errors);
  }
  if (!accountTypeValidation.isValid) {
    allErrors.push(...accountTypeValidation.errors);
  }
  if (!bankValidation.isValid) {
    allErrors.push(...bankValidation.errors);
  }
  if (!branchCodeValidation.isValid) {
    allErrors.push(...branchCodeValidation.errors);
  }
  if (!reasonValidation.isValid) {
    allErrors.push(...reasonValidation.errors);
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
};

/**
 * Validate suspend/reactivate payload
 * @param data - The suspend/reactivate data
 * @returns ValidationResult
 */
export const validateMandateStatusChange = (data: {
  Reason: string;
}): ValidationResult => {
  return validateReason(data.Reason);
};

/**
 * Check user permission for operation
 * @param userRole - The user's role
 * @param operation - The operation to check
 * @returns boolean
 */
export const checkPermission = (
  userRole: string,
  operation: "amount" | "collectionDay" | "account" | "suspend" | "reactivate" | "cancel"
): boolean => {
  const permissions: Record<string, string[]> = {
    amount: ["Super Admin", "Admin", "Finance", "Data Capturer"],
    collectionDay: ["Super Admin", "Admin", "Finance", "Data Capturer"],
    account: ["Super Admin", "Admin", "Finance", "Data Capturer"],
    suspend: ["Super Admin", "Admin", "Finance"],
    reactivate: ["Super Admin", "Admin", "Finance"],
    cancel: ["Super Admin", "Admin", "Finance", "Data Capturer", "Agent"],
  };

  return permissions[operation]?.includes(userRole) || false;
};

/**
 * Format API error messages for user display
 * @param error - The error object
 * @returns string
 */
export const formatApiError = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message && typeof error.message === "string") {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error?.data && typeof error.data === "string" && error.data.includes("404")) {
    return error.data;
  }
  return "An unexpected error occurred. Please try again.";
};

/**
 * Format success messages for user display
 * @param operation - The operation that was performed
 * @returns string
 */
export const formatSuccessMessage = (
  operation: "amount" | "collectionDay" | "account" | "suspend" | "reactivate" | "cancel"
): string => {
  const messages: Record<string, string> = {
    amount: "Amount adjusted successfully. Next collection will use the new amount.",
    collectionDay: "Collection day changed successfully. Collections will resume on the new date.",
    account: "Account details changed successfully. Customer will need to authenticate via their banking app.",
    suspend: "Mandate suspended successfully. No collections will be processed until reactivated.",
    reactivate: "Mandate reactivated successfully. Collections will resume on the next due date.",
    cancel: "Mandate cancelled successfully.",
  };

  return messages[operation] || "Operation completed successfully.";
};
